
















































































import {
  Component, Prop, Vue,
} from 'vue-property-decorator'
import { debounce } from 'vue-debounce'
import api from '@/api'
import Status from '@/utils/Status'
import StatusFlash from '@/components/StatusFlash/index.vue'
import FacetSidebar from '@/components/FacetSidebar/index.vue'
import SearchResultList from '@/components/SearchResultList/index.vue'
import Pagination from '@/components/Pagination/index.vue'
import facetFields from '@/search/facetFields'
import { buildFacetFilter, mergeFacetDistribution, buildHitMetadata } from '@/search/searchQuery'
import _ from 'lodash'

const HITS_PER_PAGE = 5

const SORT_OPTIONS = [
  { label: 'Relevance', value: '' },
  { label: 'Title (A-Z)', value: 'title:asc' },
  { label: 'Title (Z-A)', value: 'title:desc' },
]

@Component({
  components: {
    StatusFlash, FacetSidebar, SearchResultList, Pagination,
  },
})
export default class FacetedSearch extends Vue {
  @Prop({ default: '' })
  readonly initialQuery: string

  // Facet values to check by default on initial load only, keyed by predicate
  // (e.g. { entityType: ['Catalog'] }). The user can still uncheck them afterwards.
  @Prop({ default: () => ({}) })
  readonly defaultFilters: Record<string, string[]>

  // Number of facets shown in the sidebar before a "Show more facets" toggle appears.
  @Prop({ default: undefined })
  readonly initialFacetCount: number

  query: string = ''

  sort: string = ''

  readonly sortOptions = SORT_OPTIONS

  filterData: any = []

  pendingChanges: boolean = false

  appliedFilters: any[] = []

  results: any = null

  totalHits: number = null

  currentPage: number = 0

  lastPage: number = 0

  searchStatus: Status = new Status()

  liveSearch: any = null

  get isEnabled(): boolean {
    return api.searchIndex.isEnabled()
  }

  get activeFilters() {
    return this.filterData.reduce((acc, filter) => {
      const checkedValues = filter.values
        .filter((value) => value.isChecked)
        .map((value) => ({
          predicate: filter.predicate,
          filterLabel: filter.label,
          value: value.value,
          label: this.valueLabel(value),
        }))
      return [...acc, ...checkedValues]
    }, [])
  }

  created(): void {
    this.query = this.initialQuery
    this.liveSearch = debounce(() => this.search(), '400ms')
    if (this.isEnabled) {
      this.init()
    }
  }

  async init() {
    try {
      this.searchStatus.setPending()
      const facetNames = facetFields.map((f) => f.predicate)
      const seed = await api.searchIndex.search('', [], facetNames)
      this.filterData = this.applyDefaultFilters(
        this.sortFilterValues(mergeFacetDistribution([], seed.facetDistribution)),
      )
      await this.search()
    } catch (error) {
      this.searchStatus.setError('Unable to load search')
    }
  }

  sortFilterValues(filterData) {
    return filterData.map((filter) => {
      const { valueOrder, sortByCount = true } = facetFields
        .find((f) => f.predicate === filter.predicate) ?? {}
      if (valueOrder) {
        return {
          ...filter,
          sortByCount,
          values: _.sortBy(filter.values, (value) => {
            const index = valueOrder.indexOf(value.value)
            return index === -1 ? valueOrder.length : index
          }),
        }
      }
      return {
        ...filter,
        sortByCount,
        values: _.orderBy(filter.values, (value) => this.valueLabel(value).toLocaleLowerCase()),
      }
    })
  }

  valueLabel(value): string {
    return value.label || value.value
  }

  applyDefaultFilters(filterData) {
    return filterData.map((filter) => {
      const defaultValues = this.defaultFilters[filter.predicate]
      if (!defaultValues) {
        return filter
      }
      return {
        ...filter,
        values: filter.values.map((value) => ({
          ...value,
          isChecked: value.isChecked || defaultValues.includes(value.value),
        })),
      }
    })
  }

  mapFilterValueIsChecked(filterData, mapIsChecked) {
    return filterData.map((filter) => ({
      ...filter,
      values: filter.values.map((value) => ({
        ...value,
        isChecked: mapIsChecked(value.value, value.isChecked, filter),
      })),
    }))
  }

  toggleFilterValue(predicate, value) {
    const toggleIsChecked = (v, isChecked, f) => (
      f.predicate === predicate && v === value ? !isChecked : isChecked
    )
    this.filterData = this.mapFilterValueIsChecked(this.filterData, toggleIsChecked)
    this.pendingChanges = true
  }

  clearFilterValue(predicate) {
    const toggleIsChecked = (v, isChecked, f) => (f.predicate === predicate ? false : isChecked)
    this.filterData = this.mapFilterValueIsChecked(this.filterData, toggleIsChecked)
    this.pendingChanges = true
  }

  clearAllFilterValues() {
    this.filterData = this.mapFilterValueIsChecked(this.filterData, () => false)
    this.currentPage = 0
    this.pendingChanges = false
    this.search()
  }

  removeAppliedFilter(predicate, value) {
    this.toggleFilterValue(predicate, value)
    this.applyFilters()
  }

  applyFilters() {
    this.currentPage = 0
    this.pendingChanges = false
    this.search()
  }

  handleQueryInput(event) {
    this.query = event.target.value
    this.currentPage = 0
    this.liveSearch()
  }

  handleSortChange(event) {
    this.sort = event.target.value
    this.currentPage = 0
    this.search()
  }

  goToPage(page) {
    this.currentPage = page
    this.search()
  }

  async search() {
    try {
      this.searchStatus.setPending()
      const facetNames = facetFields.map((f) => f.predicate)
      const filter = buildFacetFilter(this.filterData)
      const appliedFilters = this.activeFilters
      const page = this.currentPage + 1
      const sort = this.sort ? [this.sort] : null
      const response = await api.searchIndex
        .search(this.query, filter, facetNames, page, HITS_PER_PAGE, sort)
      this.results = response.hits.map((hit) => ({
        uri: hit.uri,
        title: hit.title,
        description: hit.description,
        types: hit.types,
        metadata: buildHitMetadata(hit),
      }))
      this.totalHits = response.totalHits
      this.appliedFilters = appliedFilters
      this.lastPage = Math.max(response.totalPages - 1, 0)
      this.filterData = this.sortFilterValues(
        mergeFacetDistribution(this.filterData, response.facetDistribution),
      )
      this.searchStatus.setDone()
    } catch (error) {
      this.searchStatus.setError('Unable to get search results')
    }
  }
}
